import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import theme from '@/utils/theme';
import AuthProvider, { useAuth } from '@/contexts/AuthContext';
import AuthForm from '@/components/AuthForm';
import Dashboard from '@/pages/Dashboard';
import ClientsPage from '@/pages/ClientsPage';
import CreateClientPage from '@/pages/CreateClientPage';
import LoansPage from '@/pages/LoansPage';
import FormBuilderPage from '@/pages/FormBuilderPage';
import PageBuilderPage from '@/pages/PageBuilderPage';
import RuleBuilderPage from '@/pages/RuleBuilderPage';
import ComponentBuilderPage from '@/pages/ComponentBuilderPage';
import ReportingPage from '@/pages/ReportingPage';
import AppMarketplacePage from '@/pages/AppMarketplacePage';
import AppBuilderPage from '@/pages/AppBuilderPage';
import TrainingPage from '@/pages/TrainingPage';
import TrainingGuideViewer from '@/pages/TrainingGuideViewer';
import CreateTrainingGuidePage from '@/pages/CreateTrainingGuidePage';
import UserSettingsPage from '@/pages/UserSettingsPage';
import UserManagementPage from '@/pages/UserManagementPage';
import CompanyConfigurationPage from '@/pages/CompanyConfigurationPage';
import DocumentsPage from '@/pages/DocumentsPage';
import TaskManagerPage from '@/pages/TaskManagerPage';
import Layout from '@/components/Layout';
import LoadingScreen from '@/components/LoadingScreen';
import FileExplorerPage from '@/pages/FileExplorerPage';
import WorkflowPage from './pages/WorkflowPage';
import WorkflowBuilderPage from './pages/WorkflowBuilderPage';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f5f5f5'
        }}>
          <h1>Something went wrong</h1>
          <p>Error: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// interface ProtectedRouteProps {
//   children: React.ReactNode;
// }

// const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
//   const { isAuthenticated, isLoading } = useAuth();

//   if (isLoading) {
//     return <LoadingScreen />;
//   }

//   if (!isAuthenticated) {
//     return <AuthForm />;
//   }

//   return <>{children}</>;
// };

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/ibuild" element={<AppBuilderPage />} />
      
      {/* Protected routes */}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/create" element={<CreateClientPage />} />
                <Route path="/loans" element={<LoansPage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/reporting" element={<ReportingPage />} />
                <Route path="/training" element={<TrainingPage />} />
                <Route path="/training/create" element={<CreateTrainingGuidePage />} />
                <Route path="/training/:id" element={<TrainingGuideViewer />} />
                <Route path="/apps" element={<AppMarketplacePage />} />
                <Route path="/files" element={<FileExplorerPage />} />
                <Route path="/tasks" element={<TaskManagerPage />} />
                <Route path="/workflows" element={<WorkflowPage />} />
                <Route path="/workflows/builder/:workflowId?" element={<WorkflowBuilderPage />} />
                <Route path="/form-builder" element={<FormBuilderPage />} />
                <Route path="/page-builder" element={<PageBuilderPage />} />
                <Route path="/rule-builder" element={<RuleBuilderPage />} />
                <Route path="/component-builder" element={<ComponentBuilderPage />} />
                <Route path="/settings" element={<UserSettingsPage />} />
                <Route path="/users" element={<UserManagementPage />} />
                <Route path="/company/config" element={<CompanyConfigurationPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          ) : (
            <AuthForm />
          )
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
